// Áreas da Central da IA (redesign de 24/09/2026, proposta A): slug da URL,
// grupo do trilho, ícone e textos. O id de cada área continua o id interno
// (o `tab` da API, a flag e o Hub usam `social`, `opportunities`...); só a URL
// e o rótulo mudam. AI_CENTER_MODULES segue a fonte das flags e do formato que
// a prévia do Workspace V2 importa: este arquivo só lê dele, nunca o altera.
import { AI_CENTER_MODULES, isAiCenterModuleEnabled } from './aiCenterModules';

export { AI_CENTER_MODULES, isAiCenterModuleEnabled };

// Grupos do trilho, na ordem da tela. Qualidade é só de administrador.
export const AI_CENTER_GROUPS = [
  {
    id: 'customer',
    labelKey: 'AI_SHELL.GROUPS.CUSTOMER',
    // D42 (27/09/2026): Comentários e Retomadas aparecem juntas como
    // Oportunidades; as filas de cada uma seguem abertas pela própria página.
    areas: ['oportunidades', 'copilot'],
  },
  {
    id: 'internal',
    labelKey: 'AI_SHELL.GROUPS.INTERNAL',
    areas: ['kanban', 'conversions', 'properties', 'calls'],
  },
  {
    id: 'quality',
    labelKey: 'AI_SHELL.GROUPS.QUALITY',
    areas: ['reports'],
    adminOnly: true,
  },
];

// `badge`: o que o trilho mostra ao lado do nome. `count` é o número de
// pendentes do panorama (Ai::CenterOverview); `usage` e `follow` são áreas
// que não geram item para aprovar (Copilot é uso, Ligações é acompanhar).
// `hot`: o número acende em azul, porque ali alguém espera resposta.
// `settings`: a categoria dos Ajustes da IA que o rodapé do trilho abre.
const AREA_DEFINITIONS = [
  {
    // Área que junta outras (members): não é módulo próprio. Aparece quando
    // alguma das filas está ligada, e o número é a soma das duas.
    id: 'oportunidades',
    slug: 'oportunidades',
    icon: 'i-ph-sparkle',
    badge: 'count',
    hot: true,
    settings: 'comentarios',
    members: ['social', 'opportunities'],
  },
  {
    id: 'social',
    slug: 'comentarios',
    icon: 'i-ph-chat-circle-text',
    badge: 'count',
    hot: true,
    settings: 'comentarios',
  },
  {
    id: 'opportunities',
    slug: 'retomadas',
    icon: 'i-ph-arrow-u-up-left',
    badge: 'count',
    hot: true,
    settings: 'retomadas',
  },
  {
    id: 'copilot',
    slug: 'copilot',
    icon: 'i-ph-magic-wand',
    badge: 'usage',
    settings: 'copilot',
  },
  {
    id: 'kanban',
    slug: 'kanban',
    icon: 'i-ph-kanban',
    badge: 'count',
    settings: 'kanban',
  },
  {
    id: 'conversions',
    slug: 'conversoes',
    icon: 'i-ph-megaphone',
    badge: 'count',
    settings: 'conversoes',
  },
  {
    id: 'properties',
    slug: 'imoveis',
    icon: 'i-ph-house-line',
    badge: 'count',
    settings: 'imoveis',
  },
  {
    id: 'calls',
    slug: 'ligacoes',
    icon: 'i-ph-phone-call',
    badge: 'follow',
    settings: 'ligacoes',
  },
  {
    id: 'reports',
    slug: 'reports',
    icon: 'i-ph-flag',
    badge: 'count',
    settings: '',
  },
];

const groupOf = id =>
  AI_CENTER_GROUPS.find(group => group.areas.includes(id))?.id || '';

export const AI_CENTER_AREAS = AREA_DEFINITIONS.map(area => {
  const key = area.id.toUpperCase();
  return {
    ...area,
    group: groupOf(area.id),
    adminOnly: Boolean(
      AI_CENTER_MODULES.find(module => module.id === area.id)?.adminOnly
    ),
    labelKey: `AI_SHELL.AREAS.${key}.TITLE`,
    descriptionKey: `AI_SHELL.AREAS.${key}.DESCRIPTION`,
  };
});

export const AI_CENTER_AREA_SLUGS = AI_CENTER_AREAS.map(area => area.slug);

export const areaById = id =>
  AI_CENTER_AREAS.find(area => area.id === id) || null;

export const areaBySlug = slug =>
  AI_CENTER_AREAS.find(area => area.slug === slug) || null;

export const slugForArea = id => areaById(id)?.slug || '';

export const areaIdForSlug = slug => areaBySlug(slug)?.id || '';

// Categorias dos Ajustes da IA (D1). `atendimento` é o nome antigo do
// Copilot nos rascunhos e continua abrindo a mesma página.
export const AI_SETTINGS_CATEGORIES = [
  'comentarios',
  'retomadas',
  'copilot',
  'kanban',
  'conversoes',
  'imoveis',
  'ligacoes',
  'modelos',
];

export const AI_SETTINGS_CATEGORY_ALIASES = { atendimento: 'copilot' };

export const normalizeSettingsCategory = category => {
  const value = AI_SETTINGS_CATEGORY_ALIASES[category] || category || '';
  return AI_SETTINGS_CATEGORIES.includes(value) ? value : '';
};

// Categoria dos Ajustes que o rodapé do trilho abre a partir da área atual.
// Reports da IA não tem categoria própria: abre o início dos Ajustes.
export const settingsCategoryFor = slug => areaBySlug(slug)?.settings || '';

// Área com members vale quando alguma delas vale (D42).
export const isAreaEnabled = (area, isEnabled) => {
  if (!area) return false;
  if (area.members) {
    return area.members.some(id => isAiCenterModuleEnabled(id, isEnabled));
  }
  return isAiCenterModuleEnabled(area.id, isEnabled);
};

// Slugs que acendem a área no trilho: o dela e o das filas que ela junta.
export const areaSlugs = area => [
  area?.slug,
  ...(area?.members || []).map(slugForArea),
];

export const canSeeArea = (area, { isEnabled, isAdmin = false }) =>
  Boolean(area) &&
  isAreaEnabled(area, isEnabled) &&
  (!area.adminOnly || isAdmin);

// Grupos do trilho só com as áreas que a conta tem e que a pessoa pode ver.
// Grupo que fica vazio some junto com o título.
export const railGroups = ({ isEnabled, isAdmin = false }) =>
  AI_CENTER_GROUPS.filter(group => !group.adminOnly || isAdmin)
    .map(group => ({
      ...group,
      areas: group.areas
        .map(areaById)
        .filter(area => canSeeArea(area, { isEnabled, isAdmin })),
    }))
    .filter(group => group.areas.length);

// Primeira área ligada, na ordem dos grupos: é para onde vai quem abre a
// Central sem dizer a área.
export const firstVisibleArea = options =>
  railGroups(options).flatMap(group => group.areas)[0] || null;

// Número da área que junta filas: a soma das pendentes das ligadas; todas
// desligadas mostra "Desligada".
const membersBadge = (area, modules) => {
  const states = area.members.map(id => modules?.[id]).filter(Boolean);
  if (states.length && states.every(state => state.enabled === false)) {
    return { type: 'off' };
  }
  const counts = states
    .filter(state => state.enabled !== false)
    .map(state => Number(state.pending))
    .filter(Number.isFinite);
  if (!counts.length) return { type: 'none' };
  const count = counts.reduce((sum, value) => sum + Math.max(0, value), 0);
  return { type: 'count', count, hot: Boolean(area.hot) && count > 0 };
};

// O que o trilho mostra ao lado da área. `modules` é o `modules` do panorama
// (GET ai_center/overview); área sem entrada lá (Copilot, Ligações, Reports)
// não tem número. Área que o panorama diz desligada mostra "Desligada".
export const railBadgeFor = (area, modules = {}) => {
  if (area?.members) return membersBadge(area, modules);
  const state = modules?.[area?.id];
  if (state && state.enabled === false) return { type: 'off' };
  if (area?.badge === 'usage') return { type: 'usage' };
  if (area?.badge === 'follow') return { type: 'follow' };
  const raw = state?.pending;
  if (raw === undefined || raw === null || !Number.isFinite(Number(raw))) {
    return { type: 'none' };
  }
  const count = Math.max(0, Number(raw));
  return { type: 'count', count, hot: Boolean(area?.hot) && count > 0 };
};
